import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, StyleSheet, RefreshControl, Animated } from 'react-native';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import ThemedText from '../../components/common/ThemedText';
import { Ionicons } from '@expo/vector-icons';
import { getDashboardSummary, DashboardSummary } from '../../lib/api/dashboard';
import { storage } from '../../utils/storage';
import LedgerSummaryCard from '../../components/dashboard/LedgerSummaryCard';
import BusinessCard from '../../components/dashboard/BusinessCard';
import WorkSummaryCard from '../../components/dashboard/WorkSummaryCard';
import NetworkSummaryCard from '../../components/dashboard/NetworkSummaryCard';
import ClubsShortcutCard from '../../components/dashboard/ClubsShortcutCard';
import ActivityCard from '../../components/dashboard/ActivityCard';
import DashboardSkeleton from '../../components/common/DashboardSkeleton';
import TodoAppPromo from '../../components/dashboard/TodoAppPromo';
import TopBar from '../../components/layout/TopBar';

interface DashboardTabProps {
  headerComponent?: React.ReactNode;
  showTopBar?: boolean;
}

const DashboardTab: React.FC<DashboardTabProps> = ({ headerComponent, showTopBar = false }) => {
  const { theme, isDark } = useTheme();
  const { user, reloadUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardSummary | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const scrollY = useRef(new Animated.Value(0)).current;
  const headerTranslate = useMemo(() => {
    if ((!showTopBar && !headerComponent) || headerHeight === 0) {
      return 0;
    }
    return scrollY.interpolate({
      inputRange: [0, headerHeight],
      outputRange: [0, -headerHeight],
      extrapolate: 'clamp',
    });
  }, [scrollY, showTopBar, headerComponent, headerHeight]);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const token = await storage.getToken();
      if (!token) {
        setError('Please login to view dashboard');
        return;
      }

      const data = await getDashboardSummary(token);
      setDashboardData(data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard';
      
      // Check if it's a deactivation error
      if (errorMessage.includes('deactivated')) {
        console.log('🚫 Deactivation detected, reloading user state...');
        // Reload user to update the role and trigger navigation to DeactivatedScreen
        await reloadUser();
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    loadDashboard(true);
  };

  if (loading && !dashboardData) {
    return (
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        {showTopBar && <TopBar title="Home" showLogo={true} />}
        {headerComponent}
        <DashboardSkeleton />
      </View>
    );
  }

  if (error && !dashboardData) {
    return (
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        {showTopBar && <TopBar title="Home" showLogo={true} />}
        {headerComponent}
        <View style={[styles.centerContent, { flex: 1 }]}>
          <Ionicons name="alert-circle-outline" size={48} color={theme.textTertiary} />
          <ThemedText style={[styles.errorText, { color: theme.textSecondary }]}>
            {error}
          </ThemedText>
        </View>
      </View>
    );
  }

  if (!dashboardData) {
    return null;
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}
    >
      {(showTopBar || headerComponent) ? (
        <Animated.View
          style={[styles.headerOverlay, { transform: [{ translateY: headerTranslate }] }]}
          onLayout={(event) => {
            const height = event.nativeEvent.layout.height;
            if (height !== headerHeight) {
              setHeaderHeight(height);
            }
          }}
        >
          {showTopBar && <TopBar title="Home" showLogo={true} />}
          {headerComponent}
        </Animated.View>
      ) : null}
      <Animated.ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          headerComponent ? { paddingTop: headerHeight + 16 } : null,
        ]}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme.accent}
          />
        }
        scrollEventThrottle={16}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: true }
        )}
      >
      {/* Welcome Message */}
      <View style={styles.welcomeContainer}>
        <ThemedText style={[styles.welcomeText, { color: theme.text }]}>
          Hello, {user?.name?.split(' ')[0] || 'User'}
        </ThemedText>
      </View>

      {/* Ledger Summary */}
      <LedgerSummaryCard
        totalCredit={dashboardData.ledger.totalCredit}
        totalDebit={dashboardData.ledger.totalDebit}
        totalBalance={dashboardData.ledger.totalBalance}
      />

      {/* ToDo App Promo */}
      <TodoAppPromo />

      {/* Business Card */}
      <BusinessCard />

      {/* Work Summary */}
      <WorkSummaryCard
        contracts={dashboardData.work.contracts || 0}
        supplies={dashboardData.work.supplies || 0}
        products={dashboardData.work.products}
        quotationsSent={dashboardData.work.quotationsSent || 0}
        quotationsAccepted={dashboardData.work.quotationsAccepted || 0}
        quotationsClosed={dashboardData.work.quotationsClosed || 0}
      />

      {/* Network Summary */}
      <NetworkSummaryCard
        connections={dashboardData.network.connections}
        pendingSent={dashboardData.network.pendingSent}
        pendingReceived={dashboardData.network.pendingReceived}
        connectionTypeBreakdown={dashboardData.network.connectionTypeBreakdown}
      />

      {/* Clubs Shortcut */}
      <ClubsShortcutCard clubs={dashboardData.clubs} />

      {/* Recent Activity */}
      <ActivityCard notifications={dashboardData.notifications} />
      </Animated.ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  headerOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 10,
  },
  centerContent: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    padding: 16,
    paddingBottom: 80, // Extra padding for FAB
  },
  errorText: {
    fontSize: 16,
    marginTop: 12,
    textAlign: 'center',
  },
  welcomeContainer: {
    marginBottom: 16,
  },
  welcomeText: {
    fontSize: 24,
    fontWeight: '700',
  },
});

export default DashboardTab;
